// src/components/pages/ProfilePage/ProfilePage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserData } from '../../services/useUserData';
import { useAppStore } from '../../store/useAppStore';
import { adminService } from '../../services/adminService';
import Loader from '../../widgets/Loader/Loader';
import BackgroundCanvasWithoutRadar from '../../widgets/canvas/BackgroundCanvasWithoutRadar';
import { ProfileIcons } from '../../assets/images/profileIcons';

import './ProfilePage.scss';

function ProfilePage() {
    const navigate = useNavigate();
    // Используем store для данных
    const { userData, loadDevices, isLoading: storeLoading, refreshAllData  } = useAppStore();

    const {
        telegramId,
        userName,
        userPhoto,
        loading: userDataLoading,
        isFromTelegram
    } = useUserData();

    const [photoError, setPhotoError] = useState(false);
    const [copied, setCopied] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const isLoading = userDataLoading || storeLoading;
    const email = useAppStore((state) => state.userData?.email || null);
    const hasEmail = Boolean(email);

    useEffect(() => {
        if (telegramId && telegramId !== 'нету айди') {
            const adminStatus = adminService.isUserAdmin(telegramId); // Используем существующий метод
            setIsAdmin(adminStatus);
        }
    }, [telegramId]);

    const handleGoToAdmin = useCallback(() => {
        navigate('/admin');
    }, [navigate]);

    // Загружаем устройства через store (с кэшированием)
    useEffect(() => {
        const loadUserDevices = async () => {
            if (telegramId && telegramId !== 'нету айди') {
                try {
                    await loadDevices(telegramId);
                } catch (err) {
                }
            }
        };

        loadUserDevices();
    }, [telegramId, loadDevices]);


    const handleCopyId = useCallback(() => {
        if (telegramId) {
            navigator.clipboard.writeText(telegramId);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    }, [telegramId]);

    const handleCopyLink = useCallback(() => {
        const link = `https://appnet.shadownet.nl/key/${userData?.url_key || 'your-key'}`;
        navigator.clipboard.writeText(link);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
    }, [userData?.url_key]);

    const handlePhotoError = useCallback(() => {
        setPhotoError(true);
    }, []);

    const getInitial = useCallback(() => {
        if (userName && userName !== 'Пользователь') {
            return userName.charAt(0).toUpperCase();
        }
        return '?';
    }, [userName]);

    const getIdDisplay = useCallback(() => {
        if (userDataLoading) return '';
        if (!telegramId) return 'нету айди';

        if (isFromTelegram) {
            return `id: ${telegramId}`;
        }

        return `id: ${telegramId}`;
    }, [telegramId, userDataLoading, isFromTelegram]);

    const handleOpenAgreement = useCallback(() => {
        window.open('https://telegra.ph/Polzovatelskoe-soglashenie-08-15-10', '_blank', 'noopener,noreferrer');
    }, []);

    if (isLoading) {
        return (
            <div className="tg-app">
                <BackgroundCanvasWithoutRadar/>
                <div className="profile-page loading">
                    <div className="loader"><Loader/></div>
                </div>
            </div>
        );
    }

    return (
        <div className="tg-app">
            <BackgroundCanvasWithoutRadar/>

            <div className="profile-page">
                {/* Полукруглый хедер с фото и ID */}
                <div className="profile-header">
                    <div className="profile-header-inner">
                        <div className="profile-header-block">
                            <div className="user-photo">
                                {isFromTelegram && userPhoto && !photoError ? (
                                    <img
                                        src={userPhoto}
                                        alt={userName}
                                        className="user-photo-img"
                                        onError={handlePhotoError}
                                    />
                                ) : (
                                    <div className="photo-placeholder">
                                        {getInitial()}
                                    </div>
                                )}
                            </div>

                            <div className="user-main">
                                <div className="user-name">
                                    {userName || 'Пользователь'}
                                </div>
                                <div className="user-id-row">
                                    <span className="user-id">
                                        {getIdDisplay()}
                                    </span>
                                    {isAdmin && (
                                        <div className="user-id" onClick={handleGoToAdmin}>Админка</div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className={`copy-id-btn ${copied ? 'copied' : ''}`}
                            onClick={handleCopyId}
                            disabled={!telegramId}
                        >
                            {copied ? <ProfileIcons.check/> : <ProfileIcons.copy/>}
                        </button>
                    </div>
                </div>

                {/* Блок email */}
                {email && (
                    <div className="user-email-card">
                        <div className="user-email-info">
                            <div className="user-email-label">Email:</div>
                            <div className="user-email-value">{email}</div>
                        </div>
                        <button
                            className="user-email-edit-btn"
                            onClick={() => navigate('/change-email')}
                        >
                            <ProfileIcons.edit/>
                        </button>
                    </div>
                )}

                {/* Раздел Оплата */}
                <div className="section-card">
                    <div className="section-card-title">Оплата</div>

                    <div className="menu-item" onClick={() => navigate('/payment-history')}>
                        <div className="menu-left">
                            <div className="menu-icon">
                                <ProfileIcons.history/>
                            </div>
                            <div>
                                <div className="menu-title">История операций</div>
                                <div className="menu-subtitle">Список ваших транзакций</div>
                            </div>
                        </div>
                        <div className="menu-right">
                            <ProfileIcons.chevronRight/>
                        </div>
                    </div>

                    <div className="menu-item" onClick={() => navigate('/referral')}>
                        <div className="menu-left">
                            <div className="menu-icon">
                                <ProfileIcons.referral/>
                            </div>
                            <div>
                                <div className="menu-title">Реферальная программа</div>
                                <div className="menu-subtitle">Получайте бонусы за приглашения</div>
                            </div>
                        </div>
                        <div className="menu-right">
                            <ProfileIcons.chevronRight/>
                        </div>
                    </div>

                    <div className="menu-item" onClick={() => navigate('/profile/promo')}>
                        <div className="menu-left">
                            <div className="menu-icon">
                                <ProfileIcons.promo/>
                            </div>
                            <div>
                                <div className="menu-title">Промокод</div>
                                <div className="menu-subtitle">Получайте бонусы от промокодов</div>
                            </div>
                        </div>
                        <div className="menu-right">
                            <ProfileIcons.chevronRight/>
                        </div>
                    </div>
                </div>

                {/* Раздел Поддержка */}
                <div className="section-card">
                    <div className="section-card-title">Поддержка</div>

                    <div className="menu-item" onClick={handleOpenAgreement}>
                        <div className="menu-left">
                            <div className="menu-icon">
                                <ProfileIcons.agreement/>
                            </div>
                            <div>
                                <div className="menu-title">Пользовательское соглашение</div>
                                <div className="menu-subtitle">Соглашение на использование</div>
                            </div>
                        </div>
                        <div className="menu-right">
                            <ProfileIcons.chevronRight/>
                        </div>
                    </div>

                    <div className="menu-item" onClick={() => navigate('/install')}>
                        <div className="menu-left">
                            <div className="menu-icon">
                                <ProfileIcons.install/>
                            </div>
                            <div>
                                <div className="menu-title">Инструкции для устройств</div>
                                <div className="menu-subtitle">Подробная инструкция для установки</div>
                            </div>
                        </div>
                        <div className="menu-right">
                            <ProfileIcons.chevronRight/>
                        </div>
                    </div>
                </div>

                <div className="spacer"></div>

                {!hasEmail && (
                    <div className="subscription-button-fixed" style={{bottom: '140px'}}>
                        <div className="subscription-button" onClick={() => navigate('/add-email')}>
                            <span className="subscription-button-text">
                                Добавить email и пароль
                            </span>
                            <span className="subscription-button-label">
                                Для входа в личный кабинет с сайта
                            </span>
                            <button className="subscription-button-copy">
                                <ProfileIcons.add/>
                            </button>
                        </div>
                    </div>
                )}

                {/* Пустой div для создания пространства */}
                <div className="spacer"></div>

                {/* Кнопка со ссылкой на подписку */}
                {userData?.url_key && (
                    <div className="subscription-button-fixed">
                        <div className="subscription-button" onClick={handleCopyLink}>
                            <span className="subscription-button-text">
                                https://appnet.shadownet.nl/key/{userData.url_key}
                            </span>
                            <span className="subscription-button-label">Ваша ссылка на личный кабинет</span>
                            <button className={`subscription-button-copy ${linkCopied ? 'copied' : ''}`}>
                                {linkCopied ? <ProfileIcons.check/> : <ProfileIcons.copy/>}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ProfilePage;